import type { PresenceStatus } from "@/services/presence";

// needsOnlinePrompt decide se, dada a PREFERÊNCIA MANUAL do agente, faz sentido
// oferecer ficar disponível ao logar.
//
// Recebe a preferência MANUAL (`last_manual_presence`: available/away) — NÃO o
// `presence_status`. Motivo: o logout força presence_status='unavailable' nos
// DOIS casos (saiu available OU away); só a preferência manual distingue. E é
// ela que a reconexão restaura: quem saiu available volta available (sem alerta);
// quem saiu away volta away (com alerta — o que queremos evitar: esquecer de
// ficar online).
//
// Invariante (guardado por teste com dentes — presence-prompt.spec.ts):
//   Só `away` dispara. `available` (volta pronto) NÃO; `unavailable` (offline
//   transitório, será restaurado) NÃO; `busy` NÃO.
export function needsOnlinePrompt(manualPresence: PresenceStatus): boolean {
  return manualPresence === "away";
}

// Flag de PENDÊNCIA do alerta, em sessionStorage. Diferente do `justLoggedIn`
// (in-memory, some no F5), este SOBREVIVE ao refresh — é o que garante que a
// mensagem, uma vez aberta no login e NÃO respondida, reapareça a cada refresh
// até o agente escolher uma das opções. É limpo SÓ quando ele escolhe (ficar
// disponível com sucesso, ou permanecer como está); nunca por refresh sozinho.
// sessionStorage some ao fechar a aba → nova sessão/login recomeça limpo.
const PENDING_KEY = "luckydesk:presence:online-prompt-pending";

export function markOnlinePromptPending(): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, "1");
  } catch {
    /* ignore (modo privado / storage indisponível) */
  }
}

export function clearOnlinePromptPending(): void {
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function isOnlinePromptPending(): boolean {
  try {
    return window.sessionStorage.getItem(PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

// shouldOpenOnlinePrompt reconcilia as duas exigências do usuário:
//  (A) "só no login, não em refresh": um refresh NÃO dispara um alerta novo por
//      si só — precisa ser um login (`wasLogin`) OU uma pendência já aberta.
//  (B) "só some quando o usuário escolher": enquanto houver pendência (não
//      respondida), o refresh REABRE o alerta (não o faz sumir).
// Abrimos quando: é atendente, o status pede o alerta, e (é login novo OU há
// uma pendência não respondida desta sessão).
export function shouldOpenOnlinePrompt(opts: {
  canAttend: boolean;
  manualPresence: PresenceStatus;
  wasLogin: boolean;
  pending: boolean;
}): boolean {
  return (
    opts.canAttend &&
    needsOnlinePrompt(opts.manualPresence) &&
    (opts.wasLogin || opts.pending)
  );
}
